import { Link, Outlet, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import type { UserRole } from "@chro/shared";
import { api, selectedCompanyId } from "../lib/api";
import { useAuth } from "../stores/auth";
import { useActiveCompany } from "../stores/active-company";
import { useSetActiveCompany } from "../components/admin/use-set-active-company";
import type { Company } from "../components/admin/shared";
import { desktopOnlyRoute } from "../components/ui/mobile-availability-page";
import "../components/admin/admin-workspace.css";

export const Route = createFileRoute("/admin")({
  component: desktopOnlyRoute(AdminLayout),
});

const TABS: { to: string; label: string; roles: UserRole[] }[] = [
  { to: "/admin/inbox", label: "Inbox", roles: ["global_admin"] },
  { to: "/admin/clients", label: "Clients", roles: ["global_admin"] },
  { to: "/admin/onboarding", label: "Onboarding", roles: ["global_admin"] },
  { to: "/admin/guide", label: "Guide", roles: ["global_admin"] },
  { to: "/admin/transactions", label: "Transactions", roles: ["global_admin"] },
  { to: "/admin/users", label: "Users", roles: ["global_admin", "company_admin"] },
  { to: "/admin/global-admins", label: "Global admins", roles: ["global_admin"] },
  { to: "/admin/billing", label: "Billing", roles: ["global_admin", "company_admin"] },
  { to: "/admin/entsoe", label: "ENTSO-E", roles: ["global_admin", "company_admin"] },
  { to: "/admin/activity", label: "Activity", roles: ["global_admin", "company_admin"] },
];

function AdminLayout() {
  const user = useAuth((s) => s.user);
  const activeCompanyId = useActiveCompany((s) => s.activeCompanyId);
  const setActiveCompany = useSetActiveCompany();

  // Re-seed from localStorage on mount (read-direction only): another browser
  // tab may have switched company while this tree was unmounted. Bare setState —
  // the public setter would write localStorage back and drop eex-* caches.
  useEffect(() => {
    const id = selectedCompanyId();
    if (id !== useActiveCompany.getState().activeCompanyId) {
      useActiveCompany.setState({ activeCompanyId: id });
    }
  }, []);

  // Company admins are pinned to their own company.
  useEffect(() => {
    if (user?.role !== "global_admin" && user?.companyId) {
      useActiveCompany.getState().setActiveCompanyId(user.companyId);
    }
  }, [user]);

  const companiesQ = useQuery({
    queryKey: ["admin", "companies"],
    enabled: user?.role === "global_admin",
    queryFn: () => api<{ companies: Company[] }>("/admin/companies"),
  });

  if (!user || (user.role !== "global_admin" && user.role !== "company_admin")) {
    return <div className="p-6 text-sm text-negRed">Forbidden</div>;
  }

  const companies = companiesQ.data?.companies ?? [];
  const tabs = TABS.filter((t) => t.roles.includes(user.role));

  return (
    <div className="operations-workspace ops-admin mx-auto max-w-[1480px] space-y-6 p-6">
      <div className="ops-heading ops-admin-heading flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Admin</h1>
          <p className="text-sm text-muted">
            {user.role === "global_admin"
              ? "Platform companies, global admins, and selected-company users."
              : `${user.companyName} users and access.`}
          </p>
        </div>
        {user.role === "global_admin" && (
          <label className="ops-admin-context text-xs text-muted">
            Active company context
            <select
              value={activeCompanyId ?? ""}
              onChange={(e) => setActiveCompany(e.target.value)}
              className="mt-1 block min-w-56 bg-transparent border border-border rounded px-2 py-1.5 text-sm text-fg"
            >
              <option value="">Select company</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <nav className="ops-admin-tabs flex flex-wrap gap-4 border-b border-border text-sm">
        {tabs.map((t) => (
          <Link
            key={t.to}
            to={t.to}
            className="-mb-px border-b-2 border-transparent pb-2 px-0.5 text-muted hover:text-fg transition-colors"
            activeProps={{ className: "border-accent text-fg font-medium" }}
          >
            {t.label}
          </Link>
        ))}
      </nav>

      <Outlet />
    </div>
  );
}
